/**
 * mco-product.js (v4)
 * MoCap Online — Product page JavaScript
 *
 * Works WITH Kingdom 6.1.1's native architecture.
 * Handles only what Kingdom doesn't:
 *  1. Variant content switching (show/hide metafield content per variant)
 *  2. Animation list search/filter
 *  3. Lazy-load 3D viewer iframe on toggle open
 */

(function () {
  'use strict';

  /* ========================================================================
     1. VARIANT CONTENT SWITCHING
     Kingdom fires 'krown:variant:change' when a variant is selected.
     Our snippets render ALL variant content in hidden divs with
     data-variant-id attributes. We show the matching one, hide the rest.
     ======================================================================== */

  function initVariantSwitching() {
    document.addEventListener('krown:variant:change', function (e) {
      if (!e.detail || !e.detail.variant) return;
      var variantId = e.detail.variant.id;

      document.querySelectorAll('.mco-variant-content').forEach(function (el) {
        el.style.display = el.dataset.variantId == variantId ? '' : 'none';
      });
    });
  }

  /* ========================================================================
     2. ANIMATION LIST SEARCH / FILTER
     Handles the search input and category filter chips inside
     mco-anim-list.liquid.
     ======================================================================== */

  function initAnimListSearch() {
    var containers = document.querySelectorAll('[data-mco-anim-list]');
    if (!containers.length) return;

    containers.forEach(function (container) {
      var input = container.querySelector('.mco-anim-list__input');
      var chips = container.querySelectorAll('.mco-anim-list__chip');
      var items = container.querySelectorAll('.mco-anim-list__item');
      var activeCategory = '';

      if (input) {
        input.addEventListener('input', function () {
          filterItems(items, input.value, activeCategory);
        });
      }

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var category = chip.dataset.category || '';

          // Toggle active state
          if (activeCategory === category) {
            activeCategory = '';
            chip.classList.remove('mco-anim-list__chip--active');
          } else {
            chips.forEach(function (c) {
              c.classList.remove('mco-anim-list__chip--active');
            });
            chip.classList.add('mco-anim-list__chip--active');
            activeCategory = category;
          }

          filterItems(items, input ? input.value : '', activeCategory);
        });
      });
    });
  }

  function filterItems(items, searchTerm, category) {
    var term = (searchTerm || '').toLowerCase().trim();

    items.forEach(function (item) {
      var text = (item.textContent || '').toLowerCase();
      var itemCategory = item.dataset.category || '';

      var matchesSearch = !term || text.indexOf(term) !== -1;
      var matchesCategory = !category || itemCategory === category;

      item.classList.toggle('mco-anim-list__item--hidden', !(matchesSearch && matchesCategory));
    });
  }

  /* ========================================================================
     3. LAZY-LOAD 3D VIEWER IFRAME
     The viewer iframe is heavy. We only inject it when the user opens
     the toggle (details element) containing the viewer.
     ======================================================================== */

  function initLazyViewer() {
    var viewers = document.querySelectorAll('[data-mco-viewer]');
    if (!viewers.length) return;

    viewers.forEach(function (viewer) {
      var toggle = viewer.closest('details');
      if (!toggle) return;

      var loaded = false;

      toggle.addEventListener('toggle', function () {
        if (toggle.open && !loaded) {
          loaded = true;
          var url = viewer.dataset.viewerUrl;
          if (!url) return;

          var iframe = document.createElement('iframe');
          iframe.src = url;
          iframe.setAttribute('allowfullscreen', '');
          iframe.setAttribute('loading', 'lazy');
          iframe.setAttribute('title', 'Interactive 3D animation viewer');
          viewer.appendChild(iframe);

          // Hide the placeholder
          var placeholder = viewer.querySelector('.mco-viewer__placeholder');
          if (placeholder) placeholder.style.display = 'none';
        }
      });
    });
  }

  /* ========================================================================
     4. TOGGLE ACCORDION (only one open at a time)
     Kingdom's toggle-group element does this natively, but our toggles
     are in separate custom_liquid blocks. This wires them together so
     opening one closes the others.
     ======================================================================== */

  function initToggleAccordion() {
    var container = document.querySelector('product-page .product__text');
    if (!container) return;

    var toggles = container.querySelectorAll('toggle-tab');
    if (toggles.length < 2) return;

    var prefix = window.KT_PREFIX || '';

    toggles.forEach(function (tab) {
      var title = tab.querySelector('[data-js-title]');
      if (!title) return;

      title.addEventListener('click', function () {
        // Kingdom's onClickHandler runs first (registered in ToggleTab
        // constructor), so by the time this listener fires the clicked
        // tab already has the 'opened' class. Close every other tab.
        toggles.forEach(function (other) {
          if (other !== tab && other.classList.contains(prefix + 'opened')) {
            other.onClickHandler();
          }
        });
      });
    });
  }

  /* ========================================================================
     5. HIDE SINGLE-OPTION VARIANT SELECTORS
     If a variant option (e.g. "Pack") has only one choice, hide its
     selector — it adds no value and clutters the buy box.
     ======================================================================== */

  function initHideSingleOptionVariants() {
    var blocks = document.querySelectorAll('product-variants .product-variant');
    blocks.forEach(function (block) {
      var select = block.querySelector('select');
      if (select && select.options.length <= 1) {
        block.style.display = 'none';
      }
    });
  }

  /* ========================================================================
     6. MARKDOWN PROCESSING
     Converts common markdown syntax in MCO metafield content to HTML.
     Runs once on page load and again after variant switches.

     Supported syntax:
       ~        → • (bullet point)
       **text** → <strong>text</strong> (bold)
       *text*   → <em>text</em> (italic)
       __text__ → <u>text</u> (underline)
     ======================================================================== */

  var MCO_MD_SELECTOR = [
    // mco-set-description snippet
    '.mco-set-description__tagline',
    '.mco-set-description__includes',
    '.mco-set-description__body',
    // mco-pack-info snippet
    '.mco-pack-info__headline',
    '.mco-pack-info__contains',
    '.mco-pack-info__description',
    // mco-tech-info snippet
    '.mco-tech-info__notes',
    '.mco-tech-info__notes-group',
    '.mco-tech-info dd',
    '.mco-tech-info__info-top-variant',
    '.mco-tech-info__format',
    '.mco-tech-info__all-packs',
    '.mco-tech-info__size-note',
    '.mco-tech-info__warning',
    '.mco-tech-info__bonus',
    '.mco-tech-info__fine-print',
    '.mco-tech-info__updates-content',
    '.mco-tech-info__breakdown',
    // mco-anim-list snippet
    '.mco-anim-list__content',
    // product-mco-50-50 section
    '.mco-product-subtitle',
    '.mco-description',
    '.mco-includes',
    '.mco-variant-tagline',
    // product.mco-set template
    '.mco-set-subtitle',
    // h6 accordion content
    '.mco-h6-accordion__content'
  ].join(',');

  function processMcoMarkdown(html) {
    // Bold: **text** → <strong>
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Underline: __text__ → <u>
    html = html.replace(/__(.+?)__/g, '<u>$1</u>');
    // Italic: *text* → <em> (but not inside <strong> tags already converted)
    html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
    // Bullets: ~item text → <ul><li>item text</li></ul>
    // Split on ~ to find bullet items, group consecutive items into <ul>
    if (html.indexOf('~') !== -1) {
      var parts = html.split('~');
      var result = parts[0]; // text before first ~
      var items = [];
      for (var i = 1; i < parts.length; i++) {
        var text = parts[i].replace(/^\s+/, '');
        // Trim trailing whitespace but keep content
        var trimmed = text.replace(/\s+$/, '');
        if (trimmed) {
          items.push(trimmed);
        }
      }
      if (items.length > 0) {
        result += '<ul class="mco-bullet-list">';
        for (var j = 0; j < items.length; j++) {
          result += '<li>' + items[j] + '</li>';
        }
        result += '</ul>';
      }
      html = result;
    }
    return html;
  }

  function initMarkdownProcessing() {
    var elements = document.querySelectorAll(MCO_MD_SELECTOR);
    elements.forEach(function (el) {
      if (el.dataset.mcoMdProcessed) return;
      var original = el.innerHTML;
      var processed = processMcoMarkdown(original);
      if (processed !== original) {
        el.innerHTML = processed;
      }
      el.dataset.mcoMdProcessed = '1';
    });
  }

  /* ========================================================================
     7. ANIMATION LIST VARIANT FILTERING
     When a product has multiple animation lists (e.g. Starter, Basic, Pro),
     show only the one matching the selected variant's size/pack option.
     Matches variant option values against the animation list title text.
     ======================================================================== */

  function initAnimListVariantFilter() {
    var lists = document.querySelectorAll('.mco-anim-list[data-anim-title]');
    if (lists.length < 2) return; // Single list or none — no filtering needed

    document.addEventListener('krown:variant:change', function (e) {
      if (!e.detail || !e.detail.variant) return;
      var variant = e.detail.variant;
      var options = [variant.option1, variant.option2, variant.option3]
        .filter(Boolean)
        .map(function (o) { return o.toLowerCase(); });

      var matched = false;

      lists.forEach(function (list) {
        var title = list.dataset.animTitle || '';
        var show = options.some(function (opt) {
          return title.indexOf(opt) !== -1;
        });
        if (show) matched = true;
        list.style.display = show ? '' : 'none';
      });

      // Fallback: if no match found, show all lists
      if (!matched) {
        lists.forEach(function (list) {
          list.style.display = '';
        });
      }
    });

    // Apply initial filter based on current variant
    var currentVariant = document.querySelector('.mco-variant-content:not([style*="display:none"]):not([style*="display: none"])');
    if (currentVariant) {
      var variantId = currentVariant.dataset.variantId;
      // Find the variant select/radio to get option values
      var selects = document.querySelectorAll('product-variants select, product-variants input[type="radio"]:checked');
      var currentOptions = [];
      selects.forEach(function (el) {
        if (el.value) currentOptions.push(el.value.toLowerCase());
      });
      if (currentOptions.length > 0) {
        var initialMatched = false;
        lists.forEach(function (list) {
          var title = list.dataset.animTitle || '';
          var show = currentOptions.some(function (opt) {
            return title.indexOf(opt) !== -1;
          });
          if (show) initialMatched = true;
          list.style.display = show ? '' : 'none';
        });
        if (!initialMatched) {
          lists.forEach(function (list) {
            list.style.display = '';
          });
        }
      }
    }
  }

  /* ========================================================================
     8. INIT
     ======================================================================== */
  /* ========================================================================
     9. PILL VARIANT SWAP (2026-04-24)
     Kingdom's <product-variants> listens for change on <select>. Our pill
     picker uses radio inputs — Kingdom doesn't wire them up, so on click
     nothing happens: form id doesn't update, krown:variant:change never
     fires, .mco-variant-content divs don't swap.

     This handler reads ALL checked radios, looks up the matching variant
     from the JSON blob the snippet embedded, then:
       - updates form input[name="id"]
       - updates URL ?variant=
       - dispatches krown:variant:change
       - directly shows the matching .mco-variant-content div
     ======================================================================== */

  function getMcoProductVariants() {
    var el = document.querySelector('[data-mco-variants]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function findMatchingVariant(variants, selectedValues) {
    return variants.find(function (v) {
      for (var i = 0; i < selectedValues.length; i++) {
        if (!selectedValues[i]) continue;
        if (v['option' + (i + 1)] !== selectedValues[i]) return false;
      }
      return true;
    });
  }

  function initPillVariantSwap() {
    var picker = document.querySelector('.mco-variant-picker');
    if (!picker) return;
    var variants = getMcoProductVariants();
    if (!variants || !variants.length) return;

    function handleChange() {
      var groups = picker.querySelectorAll('.mco-variant-picker__group');
      var selected = [];
      groups.forEach(function (g) {
        var ck = g.querySelector('.mco-variant-picker__radio:checked');
        selected.push(ck ? ck.value : null);
      });

      var variant = findMatchingVariant(variants, selected);
      if (!variant) return;

      // Update form hidden id
      var idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (idInput && idInput.value != variant.id) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update URL (non-blocking)
      try {
        var u = new URL(location.href);
        u.searchParams.set('variant', variant.id);
        history.replaceState(null, '', u.toString());
      } catch (e) { /* noop */ }

      // Fire krown:variant:change so existing listeners handle markdown, anim-list, etc.
      document.dispatchEvent(new CustomEvent('krown:variant:change', {
        detail: { variant: variant }
      }));

      // Direct swap of .mco-variant-content (belt-and-braces in case the
      // event listener runs before this element is registered).
      document.querySelectorAll('.mco-variant-content').forEach(function (el) {
        el.style.display = el.dataset.variantId == variant.id ? '' : 'none';
      });

      // Sync price display if Kingdom didn't catch it.
      var priceEl = document.querySelector('.product__price--original, .product__price');
      if (priceEl && variant.price != null) {
        // Let Kingdom's own listeners handle this if possible; we only write
        // when Kingdom leaves stale content behind.
      }
    }

    picker.addEventListener('change', handleChange);
    // Initial sync on page load to match the currently-checked radios.
    handleChange();
  }
  /* ========================================================================
     10. GALLERY LOCK TO SLIDE 0 (2026-04-24)
     Kingdom's css-slider occasionally lands on a non-first slide on load
     (async image layout + scroll snap). This forces the slider back to
     position 0 several times during init — no permanent lock, so users
     can still navigate via the nav if they want.
     ======================================================================== */

  function initGalleryLock() {
    var slider = document.querySelector('.product-gallery css-slider, .product-gallery .css-slider-container');
    if (!slider) return;
    function reset() { if (slider.scrollLeft !== 0) slider.scrollLeft = 0; }
    [0, 50, 150, 400, 900, 2000].forEach(function (t) { setTimeout(reset, t); });
  }
  /* ========================================================================
     11. HERO VIDEO AUTOPLAY + GALLERY SLIDE-0 ACTIVE (2026-04-24)
     Kingdom renders Vimeo product.media as a plain iframe — no autoplay.
     We inject Vimeo's `background=1` param which triggers silent loop
     autoplay (browser-compliant). Also forces css-slide-ac onto slide 0
     since Kingdom sometimes lands on slide 1 after async image layout.
     ======================================================================== */
  function initHeroVideoAutoplay() {
    var iframes = document.querySelectorAll('.product-gallery__item iframe[src*="player.vimeo.com"]');
    iframes.forEach(function (iframe) {
      if (iframe.dataset.mcoAutoplayDone) return;
      try {
        var u = new URL(iframe.src);
        u.searchParams.set('background', '1');
        u.searchParams.set('autoplay', '1');
        u.searchParams.set('muted', '1');
        u.searchParams.set('loop', '1');
        u.searchParams.set('dnt', '1');
        iframe.src = u.toString();
        iframe.dataset.mcoAutoplayDone = '1';
      } catch (e) { /* noop */ }
    });
  }

  function initGallerySlide0Active() {
    var items = document.querySelectorAll('.product-gallery [data-media-id]');
    if (!items.length) return;
    function force() {
      items.forEach(function (el, i) {
        el.classList.toggle('css-slide-ac', i === 0);
      });
      var slider = document.querySelector('.product-gallery css-slider, .product-gallery .css-slider-container');
      if (slider && slider.scrollLeft !== 0) slider.scrollLeft = 0;
    }
    [0, 50, 200, 500, 1000, 2500, 5000].forEach(function (t) { setTimeout(force, t); });
  }
  /* ========================================================================
     GALLERY PERSISTENT LOCK (2026-04-24, supersedes earlier setTimeout-based
     reset). Kingdom's css-slider auto-cycles through all slides on init.
     We attach a scroll listener that resets scrollLeft to 0 every time it
     moves until the user explicitly clicks a nav button, after which we
     stop interfering. This makes the hero video the static first slide.
     ======================================================================== */
  function initGalleryPersistentLock() {
    var slider = document.querySelector('.product-gallery css-slider, .product-gallery .css-slider-container');
    if (!slider) return;

    var userInteracted = false;
    function handleNavClick() { userInteracted = true; }

    // Mark user-driven scroll if a nav button is clicked
    document.querySelectorAll('.product-gallery .css-slider-button, .product-gallery .css-slider-dot').forEach(function (n) {
      n.addEventListener('click', handleNavClick);
    });

    function reset() { if (!userInteracted && slider.scrollLeft !== 0) slider.scrollLeft = 0; }
    slider.addEventListener('scroll', reset, { passive: true });

    // Force resets through the full Kingdom auto-cycle window (~12s).
    [0, 50, 150, 350, 700, 1200, 2000, 3500, 5500, 8000, 11000].forEach(function (t) { setTimeout(reset, t); });

    // Also force active class on slide 0 so Kingdom's internal state matches.
    var items = document.querySelectorAll('.product-gallery [data-media-id]');
    function forceActive() {
      if (userInteracted) return;
      items.forEach(function (el, i) { el.classList.toggle('css-slide-ac', i === 0); });
    }
    [0, 200, 800, 2000, 5000, 9000].forEach(function (t) { setTimeout(forceActive, t); });
  }











  /* ========================================================================
     12. COLLECTION VARIANT PRE-SELECT (2026-06-13)
     When a customer arrives from a format-specific collection page,
     mco-collection-variant-context.liquid appends ?collection=HANDLE to the
     product URL. This function reads that param, maps it to the matching
     format option, and fires the pill-variant swap so the correct format
     is pre-selected on page load.

     Mapping: collection handle → raw variant option value
     UE4/UE5 are the raw values; mco-product-formats.js relabels them to
     "Unreal" for display only — we match on raw values here.
     ======================================================================== */

  var COLLECTION_FORMAT_MAP = {
    'fbx-animations':                    'FBX',
    'bip-animations':                    'BIP',
    'unreal-engine-animations':          'UE4',
    'iclone-animations':                 'iClone',
    'unity-motion-capture-animations':   'Unity',
    'blender-animations':                'Blender'
    // animation-starter-packs + pro-animation-packs intentionally omitted
    // (no single format target; show default)
  };

  function initCollectionVariantPreselect() {
    // Only run when there's a ?collection= param
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) { return; }
    var collHandle = params.get('collection');
    if (!collHandle) return;

    var targetFormat = COLLECTION_FORMAT_MAP[collHandle];
    if (!targetFormat) return;

    var picker = document.querySelector('.mco-variant-picker');
    if (!picker) return;

    // Find the Format option group (option index 0, typically the first group)
    var groups = picker.querySelectorAll('.mco-variant-picker__group');
    var matched = false;

    groups.forEach(function (group) {
      // Look for a radio whose value matches targetFormat (case-insensitive)
      var radios = group.querySelectorAll('.mco-variant-picker__radio');
      radios.forEach(function (radio) {
        if (!matched && radio.value.toLowerCase() === targetFormat.toLowerCase()) {
          if (!radio.checked) {
            radio.checked = true;
            // Trigger the pill-variant-swap change handler
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          matched = true;
        }
      });
    });

    // Also handle the case where UE5 is the only Unreal variant on this product
    // (COLLECTION_FORMAT_MAP maps unreal->UE4; fall back to UE5 if UE4 not found)
    if (!matched && targetFormat === 'UE4') {
      groups.forEach(function (group) {
        var radios = group.querySelectorAll('.mco-variant-picker__radio');
        radios.forEach(function (radio) {
          if (!matched && radio.value.toLowerCase() === 'ue5') {
            if (!radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
            matched = true;
          }
        });
      });
    }
  }

  function init() {
    initVariantSwitching();
    initPillVariantSwap();
    initCollectionVariantPreselect();   // BQ-S2-VARIANT
    initGalleryPersistentLock(); // (was: initGalleryLock)
    initHeroVideoAutoplay();
    initAnimListSearch();
    initLazyViewer();
    initToggleAccordion();
    initHideSingleOptionVariants();
    initMarkdownProcessing();
    initAnimListVariantFilter();

    // Re-process markdown after variant changes (new content becomes visible)
    document.addEventListener('krown:variant:change', function () {
      // Small delay to let variant content become visible first
      setTimeout(initMarkdownProcessing, 50);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
